import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	Anchor,
	DateString,
	Icon,
	IconAndText,
	Space,
	TagList,
	TagModel,
	PipeSeparator
} from "client/Components";
import {
	HubItemMediaKind,
	HubItemMediaPreviewViewModel
} from "client/Api/Hub/common";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { ActiveFilter } from "client/Features/Hub/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { localize } from "client_legacy/ndaw/culture";
import { PreviewItemUserModel } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import { ensureNotNil, setTrackingId } from "client/Misc/utils";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import styles from "./HubCardItem.module.less";
import { TagViewModel } from "client/Api/Tags/tags";
import { TimeZone } from "client/DateAndTime/timezones";
import classNames from "classnames";
import { CardStatus } from "client/Features/Workspace/Tiles/Hub/Card/CardStatus/CardStatus";

type HubCardItemProps = {
	title: string;
	descriptionPreview: string | null;
	media: HubItemMediaPreviewViewModel;
	lastModifiedDate: Date | string;
	category: HubItemCategoryModel | undefined;
	numberOfViews: number;
	author: PreviewItemUserModel | null;
	tags: readonly TagViewModel[];
	trackingId: string;
	isPinned: boolean;
	onNavigate(): void;
	onActiveFilterChange(filter: ActiveFilter): void;
	onSelectedCategoryIdChange(selectedCategoryId: number | null): void;
	timezone: TimeZone;
	isNew: boolean;
};

export const HubCardItem = (props: HubCardItemProps): ReactElement => {
	const {
		title,
		descriptionPreview,
		media,
		lastModifiedDate,
		category,
		numberOfViews,
		author,
		tags,
		onNavigate,
		onActiveFilterChange,
		onSelectedCategoryIdChange,
		trackingId,
		isPinned,
		timezone,
		isNew
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const categorySelectHandler = useCallback(
		(category: HubItemCategoryModel): void => {
			onSelectedCategoryIdChange(category.id);
		},
		[onSelectedCategoryIdChange]
	);

	const tagClickHandler = useCallback(
		(id: number): void => {
			const tag: TagViewModel = ensureNotNil(
				tags
					.find(x => x.id === id)
			);

			onActiveFilterChange({
				kind: "tag",
				value: {
					...tag,
					itemKind: HubItemKind.KnowledgeItem
				}
			});
		},
		[
			onActiveFilterChange,
			tags
		]
	);

	const tagsToRender = useMemo(
		(): readonly TagModel<number>[] => tags
			.map(t => ({
				value: t.id,
				text: t.name,
				color: "secondary"
			})),
		[tags]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
			onClick={onNavigate}
		>
			<div className={classNames(
				styles.thumbnail,
				{[styles.thumbnail_hasBorder]: media.mediaKind === HubItemMediaKind.Image}
			)}>
				<div className={styles.thumbnailScaleContainer}>
					<HubThumbnail
						title={title}
						media={media}
						hubItemKind={HubItemKind.KnowledgeItem}
						mediaPlaceholderSize="large"
					/>
				</div>
				{isNew && (
					<div className={styles.new}>
						<CardStatus
							trackingId="status"
						>
							{localize("_New")}
						</CardStatus>
					</div>
				)}
			</div>

			<h4 className={styles.title}>
				{isPinned && (
					<span
						{...setTrackingId("pinned")}
						className={styles.titleIcon}
					>
						<Icon
							size="medium"
							value={
								{
									kind:"font",
									name:"thumbtack",
									color:"primary"
								}
							}
						/>
					</span>
				)}
				<Anchor
					trackingId="title"
					className={styles.titleLink}
					title={title}
				>
					{title}
				</Anchor>
			</h4>

			<div className={styles.subtitle}>
				<span className={styles.subtitleIcon}>
					<Icon value="pen" />
				</span>
				<DateString
					value={lastModifiedDate}
					format="RelativeTimestamp"
					timezone={timezone}
				/>
				{author && (
					<>
						<PipeSeparator />
						{localize("_(Edited)By")}<Space />
						<SubtitleLink
							trackingId="author"
							text={author.name}
							onClick={
								() => onActiveFilterChange({
									kind: "author",
									value: author
								})
							}
						/>
					</>
				)}
				{category && (
					<>
						<PipeSeparator />
						<SubtitleLink
							onClick={() => categorySelectHandler(category)}
							text={category.name}
							trackingId="category"
						/>
					</>
				)}
			</div>

			<div
				{...setTrackingId("content")}
				className={styles.content}
			>
				<div className={styles.contentInner}>
					{descriptionPreview}
				</div>
			</div>

			<div className={styles.footer}>
				<div
					className={styles.tags}
					onClick={e => e.stopPropagation()}
				>
					<TagList
						trackingId="knowledge-item-tag-list"
						tags={tagsToRender}
						onTagClick={tagClickHandler}
						showMoreTagsColor="secondary"
					/>
				</div>
				<div className={styles.statisticsContainer}>
					<span {...setTrackingId("views")}>
						<IconAndText
							text={statisticsFormatter(numberOfViews)}
						>
							<IconAndText.Icon
								value="eye"
							/>
						</IconAndText>
					</span>
				</div>
			</div>
		</div>
	);
};

HubCardItem.displayName = "HubCardItem";

type SubtitleLinkProps = {
	text: string;
	onClick: () => void;
	trackingId: string;
};

const SubtitleLink = (
	props: SubtitleLinkProps
): ReactElement => {
	const {
		text,
		onClick,
		trackingId
	} = props;

	const clickHandler = (args: React.MouseEvent): void => {
		args.stopPropagation();
		onClick();
	};

	return (
		<Anchor
			trackingId={trackingId}
			className={styles.subtitleLink}
			onClick={clickHandler}
			title={text}
		>
			{text}
		</Anchor>
	);
};
SubtitleLink.displayName = "SubtitleLink";